import React, { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';

import Heading from '@splunk/react-ui/Heading';
import Tooltip from '@splunk/react-ui/Tooltip';

import { CursorState } from './cursorPlugin';
import type { PlotUtilities, SeriesInfo } from './renderers/types';
import { useVizTheme } from './theme';
import type {
    LineDashStyle,
    ScaleConfig,
    SeriesConfig,
    MarkAnnotation,
    GenericDataSet,
    LegendTruncation,
} from './types';
import {
    buildSeriesForLegend,
    exists,
    formatValueWithSeriesConfig,
    getMergedSeriesConfigForField,
} from './utilities/utils';
import { getLineDashArray } from './renderers/uplot-utils';

type Dimensions = {
    width?: number;
    height?: number;
};

type OnSeriesClick = (field: string, ref: React.Ref<HTMLElement>) => void;
type OnSeriesHover = (field: string | null) => void;

export type LegendComponentProps = {
    cursorState?: CursorState;
    data: GenericDataSet;
    plotUtilities?: PlotUtilities;
    seriesConfig?: SeriesConfig;
    scaleConfig?: ScaleConfig;
    hideSeriesValue?: boolean;
    onSeriesClick: OnSeriesClick;
    onSeriesHover: OnSeriesHover;
    seriesDataAccessor?: (seriesIndex: number) => string | number | null;
    title?: string;
    reversedOrder?: boolean;
    fontSize?: number;
    marks?: MarkAnnotation[];
    anchor?: 'right' | 'left' | 'top' | 'bottom';
    center?: boolean;
    largerTiles?: boolean;
    legendLabels?: string[];
    isSeriesCompare?: boolean;
    legendTruncation?: LegendTruncation;
};

export type LegendComponent<AdditionalProps = Record<string, unknown>> = React.ComponentType<
    LegendComponentProps & AdditionalProps
>;

export type Legend = {
    component: LegendComponent;
    size: Dimensions;
    anchor: 'right' | 'bottom' | 'left' | 'top';
};

type LegendSeriesProps = {
    series: SeriesInfo;
    defocus: boolean;
    onSeriesClick: OnSeriesClick;
    onSeriesHover: OnSeriesHover;
    formattedValue: React.ReactNode;
    fontSize?: number;
    largerTiles?: boolean;
    isSeriesCompare?: boolean;
    legendTruncation?: LegendTruncation;
    lineDashStyle?: LineDashStyle;
    seriesType?: SeriesConfig[string]['type'];
    markerDisplay?: 'off' | 'filled' | 'outlined';
};

type LegendSeriesItemButtonProps = {
    fontSize?: number;
    show?: boolean;
    defocus?: boolean;
    largerTiles?: boolean;
};

const StyledTooltip = styled(Tooltip)`
    flex-shrink: 0;
`;

const DEFAULT_BOTTOM_LEGEND_HEIGHT = 35;
const INACTIVE_LEGEND_OPACITY = 0.2;
const ACTIVE_LEGEND_VALUE_OPACITY = 0.6;
// Highcharts' default legend.symbolWidth is 16; keep canvas line symbols in the same footprint.
const LEGEND_LINE_TILE_WIDTH = 16;
const LEGEND_LINE_LENGTH = 16;
const LEGEND_LINE_STROKE_WIDTH = 1.5;
const LEGEND_LINE_TILE_HEIGHT = 12;
const LEGEND_LINE_TILE_LARGE_HEIGHT = 16;
// Splunk charting's Highcharts line marker defaults are radius 2; hollow radius is lineWidth * 1.25.
const LEGEND_FILLED_MARKER_RADIUS = 2;
const LEGEND_OUTLINED_MARKER_RADIUS = 1.875;
const LEGEND_OUTLINED_MARKER_STROKE_WIDTH = LEGEND_LINE_STROKE_WIDTH;

type TileProps = {
    largerTiles: boolean;
    color: string;
};

const LegendSeriesItemButton = styled.button<LegendSeriesItemButtonProps>`
    display: flex;
    flex-direction: row;
    align-items: center;
    cursor: pointer;
    font-size: ${({ fontSize, largerTiles }) => `${largerTiles ? 12 : fontSize || 10}px`} !important;
    > * {
        opacity: ${({ show, defocus }) => (show && !defocus ? 1 : INACTIVE_LEGEND_OPACITY)} !important;
    }
    height: ${({ largerTiles }) => (largerTiles ? 16 : 13)}px;
    margin-bottom: ${({ largerTiles }) => (largerTiles ? '4px' : '0')};
    border: 0;
    background: transparent;
    padding: 0;
    text-align: left;
    width: 100%;
    min-width: 0;
    color: inherit;
    font: inherit;
    appearance: none;
    outline: none;
    /* Keep a visible focus ring for keyboard users, but suppress the default
       outline left behind after a pointer click (which appeared as a stray
       white box around the toggled legend item on dark themes). */
    &:focus-visible {
        outline: revert;
    }
`;

const LegendValue = styled.div<{ show?: boolean; defocus?: boolean }>`
    opacity: ${({ show, defocus }) =>
        show && !defocus ? ACTIVE_LEGEND_VALUE_OPACITY : INACTIVE_LEGEND_OPACITY} !important;
    margin-left: 4px;
    flex-shrink: 0;
`;

const LegendTile = styled.div<TileProps>`
    display: flex;
    position: relative;
    border-radius: 1px;
    background-color: ${({ color }) => color};
    margin-top: 1px;
    margin-right: ${({ largerTiles }) => (largerTiles ? 4 : 5)}px;
    width: ${({ largerTiles }) => (largerTiles ? 16 : 6)}px;
    height: ${({ largerTiles }) => (largerTiles ? 16 : 11)}px;
    flex-shrink: 0;
`;

const LegendLineTile = ({
    color,
    lineDashStyle,
    largerTiles,
    markerDisplay,
    markerFillColor,
}: {
    color: string;
    lineDashStyle?: LineDashStyle;
    largerTiles?: boolean;
    markerDisplay?: 'off' | 'filled' | 'outlined';
    markerFillColor?: string;
}): React.ReactElement => {
    const width = LEGEND_LINE_TILE_WIDTH;
    const height = largerTiles ? LEGEND_LINE_TILE_LARGE_HEIGHT : LEGEND_LINE_TILE_HEIGHT;
    const midY = height / 2;
    const lineStartX = (width - LEGEND_LINE_LENGTH) / 2;
    const lineEndX = lineStartX + LEGEND_LINE_LENGTH;
    const rawDashArray = getLineDashArray(lineDashStyle);
    // Scale only patterns that are longer than the tile, so common dash styles keep
    // their chart proportions without making the legend item wider.
    let scaledDashArray = rawDashArray;
    if (rawDashArray) {
        const totalCycle = rawDashArray.reduce((sum, v) => sum + v, 0);
        const maxCycleLength = LEGEND_LINE_LENGTH;
        if (totalCycle > maxCycleLength) {
            const scale = maxCycleLength / totalCycle;
            scaledDashArray = rawDashArray.map(v => Math.max(v * scale, 0.5));
        }
    }
    const strokeDasharray = scaledDashArray
        ? scaledDashArray.map(v => Number(v.toFixed(2)).toString()).join(' ')
        : undefined;
    const showMarker = markerDisplay === 'filled' || markerDisplay === 'outlined';
    const markerRadius =
        markerDisplay === 'outlined' ? LEGEND_OUTLINED_MARKER_RADIUS : LEGEND_FILLED_MARKER_RADIUS;
    const markerStrokeWidth = markerDisplay === 'outlined' ? LEGEND_OUTLINED_MARKER_STROKE_WIDTH : 0;
    return (
        <svg
            data-test="legend-series-item-tile"
            width={width}
            height={height}
            style={{ marginRight: 4, flexShrink: 0, display: 'block' }}
        >
            <line
                x1={lineStartX}
                y1={midY}
                x2={lineEndX}
                y2={midY}
                stroke={color}
                strokeWidth={LEGEND_LINE_STROKE_WIDTH}
                strokeDasharray={strokeDasharray}
            />
            {showMarker && (
                <circle
                    cx={width / 2}
                    cy={midY}
                    r={markerRadius}
                    fill={markerDisplay === 'filled' ? color : markerFillColor}
                    stroke={color}
                    strokeWidth={markerStrokeWidth}
                />
            )}
        </svg>
    );
};

/**
 * Gets CSS styles for label truncation based on the truncation type.
 * - ellipsisEnd: standard text-overflow ellipsis (default)
 * - ellipsisStart: uses direction: rtl trick for start truncation
 * - ellipsisMiddle: handled via JS with measurement
 * - ellipsisOff: no truncation, text clips at boundary
 */
const getTruncationStyles = (truncation: LegendTruncation | undefined): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
    };

    switch (truncation) {
        case 'ellipsisOff':
            return {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'clip',
            };
        case 'ellipsisStart': // CSS does not natively support start/middle ellipsis; both fall back to clip
        case 'ellipsisMiddle':
            return {
                ...baseStyles,
                textOverflow: 'clip',
            };
        case 'ellipsisEnd':
        default:
            return {
                ...baseStyles,
                textOverflow: 'ellipsis',
            };
    }
};

/**
 * Cached canvas for text measurement to avoid creating new canvases on every call.
 */
let measureCanvas: HTMLCanvasElement | null = null;
let measureCtx: CanvasRenderingContext2D | null = null;

/**
 * Resets the cached canvas context. Primarily used for testing.
 * @internal
 */
export const resetMeasureCanvas = (): void => {
    measureCanvas = null;
    measureCtx = null;
};

/**
 * Gets or creates the cached canvas context for text measurement.
 */
const getMeasureContext = (): CanvasRenderingContext2D | null => {
    if (!measureCanvas) {
        measureCanvas = document.createElement('canvas');
        measureCtx = measureCanvas.getContext('2d');
    }
    return measureCtx;
};

/**
 * Truncates a label from the start, showing the end with ellipsis at the beginning.
 * Uses canvas measurement to calculate the optimal truncation point.
 * @internal
 */
export const truncateStart = (label: string, maxWidth: number, font: string): string => {
    if (!label || maxWidth <= 0) return label || '';

    // Use cached canvas for measuring text width
    const ctx = getMeasureContext();
    if (!ctx) return label;

    ctx.font = font;
    const fullWidth = ctx.measureText(label).width;

    if (fullWidth <= maxWidth) return label;

    const ellipsis = '…';
    const ellipsisWidth = ctx.measureText(ellipsis).width;
    const availableWidth = maxWidth - ellipsisWidth;

    if (availableWidth <= 0) return ellipsis;

    // Find how many characters from the end fit
    let endLen = label.length;
    while (endLen > 0) {
        const endPart = label.slice(-endLen);
        const textWidth = ctx.measureText(endPart).width;

        if (textWidth <= availableWidth) {
            return ellipsis + endPart;
        }
        endLen -= 1;
    }

    return ellipsis;
};

/**
 * Truncates a label from the middle, showing start and end with ellipsis in between.
 * Uses canvas measurement to calculate the optimal truncation point.
 */
const truncateMiddle = (label: string, maxWidth: number, font: string): string => {
    if (!label || maxWidth <= 0) return label || '';

    // Use cached canvas for measuring text width
    const ctx = getMeasureContext();
    if (!ctx) return label;

    ctx.font = font;
    const fullWidth = ctx.measureText(label).width;

    if (fullWidth <= maxWidth) return label;

    const ellipsis = '…';
    const ellipsisWidth = ctx.measureText(ellipsis).width;
    const availableWidth = maxWidth - ellipsisWidth;

    if (availableWidth <= 0) return ellipsis;

    // Binary search for the optimal split point
    let startLen = Math.floor(label.length / 2);
    let endLen = Math.floor(label.length / 2);

    // Start with roughly equal portions and adjust
    while (startLen > 0 || endLen > 0) {
        const startPart = label.slice(0, startLen);
        const endPart = label.slice(-endLen);
        const totalWidth = ctx.measureText(startPart).width + ctx.measureText(endPart).width;

        if (totalWidth <= availableWidth) {
            return startPart + ellipsis + endPart;
        }

        // Remove characters alternately from longer portion
        if (startLen >= endLen && startLen > 0) {
            startLen -= 1;
        } else if (endLen > 0) {
            endLen -= 1;
        }
    }

    return ellipsis;
};

const LegendSeriesItem: React.FC<LegendSeriesProps> = ({
    series,
    defocus,
    onSeriesClick,
    onSeriesHover,
    formattedValue,
    fontSize,
    largerTiles,
    isSeriesCompare,
    legendTruncation,
    lineDashStyle,
    seriesType,
    markerDisplay,
}) => {
    const { field, label, fill, stroke, show } = series;
    const [isOverflowed, setIsOverflowed] = useState(false);
    const [displayLabel, setDisplayLabel] = useState(label || '');
    const buttonRef = useRef<HTMLButtonElement>(null);
    const labelRef = useRef<HTMLSpanElement>(null);
    const theme = useVizTheme();

    // Handle start and middle truncation
    React.useLayoutEffect(() => {
        if (
            (legendTruncation === 'ellipsisMiddle' || legendTruncation === 'ellipsisStart') &&
            labelRef.current &&
            label
        ) {
            const element = labelRef.current;
            const computedStyle = window.getComputedStyle(element);
            const font = computedStyle.font || `${fontSize || 10}px ${computedStyle.fontFamily}`;
            const maxWidth = element.clientWidth;

            if (maxWidth > 0) {
                const truncated =
                    legendTruncation === 'ellipsisStart'
                        ? truncateStart(label, maxWidth, font)
                        : truncateMiddle(label, maxWidth, font);
                setDisplayLabel(truncated);
            }
        } else {
            setDisplayLabel(label || '');
        }
    }, [label, legendTruncation, fontSize]);

    const handleClick = useCallback(() => {
        onSeriesClick(field, buttonRef);
    }, [onSeriesClick, field]);

    // Prevent the button from taking focus on a pointer click. Otherwise the
    // toggled legend item keeps focus and renders a stray focus ring (the
    // "white box" on dark themes). Keyboard navigation is unaffected, so the
    // focus-visible ring is still shown for Tab users (accessibility).
    const handleMouseDown = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    }, []);

    const handleMouseEnter = useCallback(() => {
        onSeriesHover(field);
        // Check label overflow when hovering the button
        if (labelRef.current) {
            const isCurrentlyOverflowed =
                legendTruncation === 'ellipsisMiddle' || legendTruncation === 'ellipsisStart'
                    ? displayLabel !== label
                    : labelRef.current.scrollWidth > labelRef.current.offsetWidth;
            setIsOverflowed(isCurrentlyOverflowed);
        }
    }, [onSeriesHover, field, legendTruncation, displayLabel, label]);

    const handleMouseLeave = useCallback(() => {
        onSeriesHover(null);
    }, [onSeriesHover]);

    const truncationStyles = getTruncationStyles(legendTruncation);

    return (
        <StyledTooltip content={label && isOverflowed ? <span>{label}</span> : ''} inline={false}>
            <LegendSeriesItemButton
                data-test="legend-series-item"
                ref={buttonRef}
                fontSize={fontSize}
                show={show}
                defocus={defocus}
                largerTiles={largerTiles}
                type="button"
                onClick={handleClick}
                onMouseDown={handleMouseDown}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {seriesType === 'line' || !seriesType ? (
                    <LegendLineTile
                        color={String(stroke ?? fill)}
                        lineDashStyle={lineDashStyle}
                        largerTiles={largerTiles}
                        markerDisplay={markerDisplay}
                        markerFillColor={theme.markerFill}
                    />
                ) : (
                    <LegendTile
                        data-test="legend-series-item-tile"
                        largerTiles={largerTiles ?? false}
                        color={fill ? String(fill) : String(stroke)}
                    />
                )}
                <span
                    ref={labelRef}
                    data-test="legend-series-item-label"
                    style={{
                        flex: 1,
                        font: theme.legendLabelFont,
                        ...(largerTiles && { fontSize: 12, lineHeight: '16px' }),
                        // margin prevents label and value having no space in a one-liner BUZZ-2150
                        ...(formattedValue && { marginRight: 3 }),
                        // Apply maxWidth only when truncation is active (not ellipsisOff)
                        ...(legendTruncation &&
                            legendTruncation !== 'ellipsisOff' && {
                                maxWidth: '150px',
                            }),
                        ...truncationStyles,
                    }}
                >
                    {legendTruncation === 'ellipsisMiddle' || legendTruncation === 'ellipsisStart'
                        ? displayLabel
                        : label}
                </span>
                {isSeriesCompare && formattedValue ? (
                    <LegendValue data-test="legend-series-item-value" show={show} defocus={defocus}>
                        {formattedValue}
                    </LegendValue>
                ) : (
                    <div>{formattedValue}</div>
                )}
            </LegendSeriesItemButton>
        </StyledTooltip>
    );
};

type LegendSeriesListProps = Omit<LegendComponentProps, 'title'> & {
    plotUtilities: PlotUtilities;
};

interface ComputeFormattedValueOptions {
    seriesIndex: number;
    field: string;
    hideSeriesValue: boolean;
    dataColumnsLength: number;
    seriesDataAccessor: (i: number) => string | number | null;
    seriesConfig: Record<string, unknown>;
    scaleConfig: Record<string, unknown>;
    data: GenericDataSet;
}

/**
 * Computes the formatted value for a legend series item
 */
const computeFormattedValue = (options: ComputeFormattedValueOptions): React.ReactNode => {
    const {
        seriesIndex,
        field,
        hideSeriesValue,
        dataColumnsLength,
        seriesDataAccessor,
        seriesConfig,
        scaleConfig,
        data,
    } = options;

    if (hideSeriesValue || seriesIndex >= dataColumnsLength - 1) {
        return null;
    }

    const value = seriesDataAccessor(seriesIndex);
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return ' ';
    }

    const config = getMergedSeriesConfigForField(field, seriesConfig, scaleConfig);
    const formatted = formatValueWithSeriesConfig(value as number, config, data);
    return formatted === 'NaN' || formatted === 'nan' ? ' ' : formatted;
};

/**
 * Determines if a series should be defocused based on cursor/hover state
 */
const shouldDefocusSeries = (
    isSeriesCompare: boolean,
    cursorSeriesIndex: number | undefined,
    hoveredSeriesKey: string | null | undefined,
    field: string,
    index: number,
    slicedSeriesLength: number,
    reversedOrder: boolean
): boolean => {
    if (isSeriesCompare) {
        return false;
    }

    if (!exists(cursorSeriesIndex) && !hoveredSeriesKey) {
        return false;
    }

    if (hoveredSeriesKey) {
        return field !== hoveredSeriesKey;
    }

    return reversedOrder ? slicedSeriesLength - index !== cursorSeriesIndex : index + 1 !== cursorSeriesIndex;
};

const LegendSeriesList = (props: LegendSeriesListProps): React.ReactElement => {
    const {
        plotUtilities,
        data,
        cursorState,
        seriesConfig = {},
        scaleConfig = {},
        hideSeriesValue,
        seriesDataAccessor = (i: number): string | number | null =>
            cursorState?.cursor ? data.columns[i + 1][cursorState.cursor.index] : null,
        fontSize,
        reversedOrder,
        onSeriesClick,
        onSeriesHover,
        marks,
        largerTiles,
        legendLabels,
        isSeriesCompare,
        legendTruncation,
    } = props;

    const slicedSeries = buildSeriesForLegend(plotUtilities.allSeries, reversedOrder, marks, legendLabels);
    const [hoveredSeriesKey, setHoveredSeriesKey] = useState<string | null>();
    const handleSeriesHover = useCallback(
        (field: string | null) => {
            setHoveredSeriesKey(field);
            onSeriesHover(field);
        },
        [onSeriesHover]
    );

    return (
        <>
            {slicedSeries.map((series, i) => {
                const { field } = series;
                const seriesIndex = reversedOrder ? slicedSeries.length - i - 1 : i;

                const formattedValue = computeFormattedValue({
                    seriesIndex,
                    field,
                    hideSeriesValue: hideSeriesValue ?? false,
                    dataColumnsLength: data.columns.length,
                    seriesDataAccessor,
                    seriesConfig,
                    scaleConfig,
                    data,
                });

                const defocus = shouldDefocusSeries(
                    isSeriesCompare ?? false,
                    cursorState?.seriesIndex,
                    hoveredSeriesKey,
                    field,
                    i,
                    slicedSeries.length,
                    reversedOrder ?? false
                );

                const fieldSeriesConfig = getMergedSeriesConfigForField(field, seriesConfig, scaleConfig);
                return (
                    <LegendSeriesItem
                        key={field}
                        series={series}
                        defocus={defocus}
                        formattedValue={formattedValue}
                        onSeriesClick={onSeriesClick}
                        onSeriesHover={handleSeriesHover}
                        fontSize={fontSize}
                        largerTiles={largerTiles}
                        isSeriesCompare={isSeriesCompare}
                        legendTruncation={legendTruncation}
                        seriesType={fieldSeriesConfig?.type}
                        lineDashStyle={fieldSeriesConfig?.lineDashStyle}
                        markerDisplay={fieldSeriesConfig?.markerDisplay}
                    />
                );
            })}
        </>
    );
};

export const SideLegendComponent: LegendComponent<{
    width?: number;
}> = props => {
    const {
        plotUtilities,
        title,
        width,
        anchor,
        center,
        largerTiles,
        isSeriesCompare,
        ...legendSeriesProps
    } = props;
    const theme = useVizTheme();

    if (!plotUtilities) {
        return null;
    }

    // Determine width styling based on whether width prop is provided.
    // In seriesCompare mode use a fixed width so legend items don't shift when
    // hover values appear/disappear — only the value text updates in place.
    let widthStyles: React.CSSProperties;
    if (width) {
        widthStyles = { width };
    } else if (isSeriesCompare) {
        widthStyles = { width: 168 };
    } else {
        widthStyles = { maxWidth: 120, minWidth: 60 };
    }

    return (
        <div
            data-test="legend-div"
            style={{
                display: 'flex',
                flexDirection: 'column',
                ...widthStyles,
                color: theme.legendLabel,
                font: theme.legendLabelFont,
                height: '100%',
                overflowX: 'hidden',
                overflowY: 'auto',
                paddingRight: 5,
                paddingLeft: anchor === 'left' ? 10 : undefined,
            }}
        >
            <div
                data-test="inner-legend-div"
                style={
                    center
                        ? {
                              marginTop: 'auto',
                              marginBottom: 'auto',
                          }
                        : {}
                }
            >
                {title && <Heading level={6}> {title} </Heading>}
                <LegendSeriesList
                    plotUtilities={plotUtilities}
                    largerTiles={largerTiles}
                    isSeriesCompare={isSeriesCompare}
                    {...legendSeriesProps}
                />
            </div>
        </div>
    );
};

export const BottomLegendComponent: LegendComponent<{
    wrap?: boolean;
    height?: number;
}> = props => {
    const { plotUtilities, title, wrap, center, anchor, largerTiles, height, ...legendSeriesProps } = props;

    const theme = useVizTheme();

    if (!plotUtilities) {
        return null;
    }

    return (
        <div>
            {title && (
                <Heading style={{ marginTop: '0' }} level={6}>
                    {title}
                </Heading>
            )}
            <div
                data-test="legend-div"
                style={{
                    display: 'flex',
                    flexWrap: wrap ? 'wrap' : 'nowrap',
                    color: theme.legendLabel,
                    gap: '0px 10px',
                    justifyContent: center ? 'center' : undefined,
                    paddingTop: anchor === 'top' ? 17 : undefined,
                    overflowX: 'auto',
                    overflowY: 'auto',
                    maxHeight: height ?? DEFAULT_BOTTOM_LEGEND_HEIGHT,
                }}
            >
                <LegendSeriesList
                    plotUtilities={plotUtilities}
                    largerTiles={largerTiles}
                    {...legendSeriesProps}
                />
            </div>
        </div>
    );
};

type SideLegendFactoryProps = {
    hideSeriesValue?: boolean;
    aggregateData?: (number | null)[];
    width?: number;
    fontSize?: number;
    title?: string;
    reversedOrder?: boolean;
    center?: boolean;
    largerTiles?: boolean;
    legendTruncation?: LegendTruncation;
};

/**
 * Helper to compute series data accessor and hideSeriesValue flag based on factory props
 */
const getSeriesDataConfig = (
    hideSeriesValue: boolean | undefined,
    aggregateData: (number | null)[] | undefined,
    cursorState: { cursor: unknown } | undefined
): { seriesDataAccessor?: (i: number) => number | null; hideSeriesValue?: boolean } => {
    if (hideSeriesValue) {
        return { seriesDataAccessor: () => null, hideSeriesValue: true };
    }

    const shouldUseAggregate = aggregateData && (!cursorState || cursorState.cursor === null);
    if (shouldUseAggregate) {
        return { seriesDataAccessor: (i: number) => aggregateData[i] };
    }

    return {};
};

export const SideLegendFactory: (props?: SideLegendFactoryProps) => Legend = props => {
    const component: LegendComponent = componentProps => {
        if (!props) {
            return <SideLegendComponent {...componentProps} />;
        }

        const {
            aggregateData,
            title,
            reversedOrder,
            width,
            fontSize,
            hideSeriesValue,
            center,
            largerTiles,
            legendTruncation,
        } = props;
        const dataConfig = getSeriesDataConfig(hideSeriesValue, aggregateData, componentProps.cursorState);

        return (
            <SideLegendComponent
                {...componentProps}
                {...(width !== undefined && { width })}
                fontSize={fontSize}
                title={title}
                reversedOrder={reversedOrder}
                center={center}
                largerTiles={largerTiles}
                legendTruncation={legendTruncation}
                {...dataConfig}
            />
        );
    };

    return {
        component,
        size: { width: props?.width ?? 200 },
        anchor: 'right',
    };
};

export const LeftLegendFactory: (props?: SideLegendFactoryProps) => Legend = props => {
    const component: LegendComponent = componentProps => {
        if (!props) {
            return <SideLegendComponent {...componentProps} anchor="left" />;
        }

        const {
            aggregateData,
            title,
            reversedOrder,
            width,
            fontSize,
            hideSeriesValue,
            center,
            largerTiles,
            legendTruncation,
        } = props;
        const dataConfig = getSeriesDataConfig(hideSeriesValue, aggregateData, componentProps.cursorState);

        return (
            <SideLegendComponent
                {...componentProps}
                {...(width !== undefined && { width })}
                fontSize={fontSize}
                title={title}
                reversedOrder={reversedOrder}
                center={center}
                largerTiles={largerTiles}
                anchor="left"
                legendTruncation={legendTruncation}
                {...dataConfig}
            />
        );
    };

    return {
        component,
        size: { width: props?.width ?? 200 },
        anchor: 'left',
    };
};

type BottomLegendFactoryProps = {
    hideSeriesValue?: boolean;
    aggregateData?: (number | null)[];
    wrap?: boolean;
    fontSize?: number;
    title?: string;
    reversedOrder?: boolean;
    height?: number;
    center?: boolean;
    largerTiles?: boolean;
    legendTruncation?: LegendTruncation;
};

export const BottomLegendFactory: (props?: BottomLegendFactoryProps) => Legend = props => {
    const component: LegendComponent = componentProps => {
        if (!props) {
            return <BottomLegendComponent {...componentProps} />;
        }

        const {
            aggregateData,
            title,
            reversedOrder,
            wrap,
            fontSize,
            hideSeriesValue,
            center,
            largerTiles,
            height,
            legendTruncation,
        } = props;
        const dataConfig = getSeriesDataConfig(hideSeriesValue, aggregateData, componentProps.cursorState);

        return (
            <BottomLegendComponent
                {...componentProps}
                wrap={wrap}
                fontSize={fontSize}
                title={title}
                reversedOrder={reversedOrder}
                center={center}
                largerTiles={largerTiles}
                height={height}
                legendTruncation={legendTruncation}
                {...dataConfig}
            />
        );
    };

    return {
        component,
        size: { height: props?.height || DEFAULT_BOTTOM_LEGEND_HEIGHT },
        anchor: 'bottom',
    };
};

export const TopLegendFactory: (props?: BottomLegendFactoryProps) => Legend = props => {
    const component: LegendComponent = componentProps => {
        if (!props) {
            return <BottomLegendComponent {...componentProps} anchor="top" />;
        }

        const {
            aggregateData,
            title,
            reversedOrder,
            wrap,
            fontSize,
            hideSeriesValue,
            center,
            largerTiles,
            legendTruncation,
        } = props;
        const dataConfig = getSeriesDataConfig(hideSeriesValue, aggregateData, componentProps.cursorState);

        return (
            <BottomLegendComponent
                {...componentProps}
                wrap={wrap}
                fontSize={fontSize}
                title={title}
                reversedOrder={reversedOrder}
                center={center}
                largerTiles={largerTiles}
                anchor="top"
                legendTruncation={legendTruncation}
                {...dataConfig}
            />
        );
    };

    return {
        component,
        size: { height: 35 },
        anchor: 'top',
    };
};

export const BottomLegend: Legend = BottomLegendFactory();
export const TopLegend: Legend = TopLegendFactory();

export const SideLegend: Legend = SideLegendFactory();
export const LeftLegend: Legend = LeftLegendFactory();

export const LegendMap: { [key in Legend['anchor']]: Legend } = {
    bottom: BottomLegend,
    right: SideLegend,
    top: TopLegend,
    left: LeftLegend,
};
